import { useEffect, useMemo, useState } from 'react';
import { ordersAPI, settingsAPI, exportAPI, downloadBlob, reviewsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Search,
  Filter,
  Eye,
  CheckCircle2,
  Truck,
  XCircle,
  MessageCircle,
  Copy,
  Loader2,
  Package,
  Calendar,
  Phone,
  MapPin,
  Store as StoreIcon,
  RefreshCw,
  Download,
  FileSpreadsheet,
  FileBarChart,
  Star as StarIcon,
} from 'lucide-react';

const STATUS_PAYMENT = {
  pending: { label: 'Aguardando', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  paid_unverified: { label: 'Cliente afirma pago', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  confirmed: { label: 'Confirmado', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  refunded: { label: 'Estornado', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
};

const STATUS_ORDER = {
  received: { label: 'Recebido', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  forwarded: { label: 'Encaminhado', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  delivered: { label: 'Entregue', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  cancelled: { label: 'Cancelado', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const fmtBRL = (v) =>
  typeof v === 'number'
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : v;

export const OrdersCentral = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [filterPayment, setFilterPayment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selected, setSelected] = useState(null);
  const [footerCfg, setFooterCfg] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await ordersAPI.list();
      data.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setOrders(data);
    } catch (err) {
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    settingsAPI.getFooter().then(({ data }) => setFooterCfg(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      if (filterPayment !== 'all' && o.payment_status !== filterPayment) return false;
      if (filterStatus !== 'all' && o.order_status !== filterStatus) return false;
      if (filterText) {
        const t = filterText.toLowerCase();
        const blob = `${o.protocol || ''} ${o.customer_name || ''} ${o.customer_phone || ''} ${o.store_name || ''}`.toLowerCase();
        if (!blob.includes(t)) return false;
      }
      return true;
    });
  }, [orders, filterText, filterPayment, filterStatus]);

  const stats = useMemo(() => {
    const total = orders.length;
    const revenue = orders.reduce((s, o) => s + (o.total || 0), 0);
    const pending = orders.filter((o) => (o.payment_status || 'pending') === 'pending').length;
    const confirmed = orders.filter((o) => o.payment_status === 'confirmed').length;
    return { total, revenue, pending, confirmed };
  }, [orders]);

  const patchOrder = async (id, payload) => {
    try {
      const { data } = await ordersAPI.patchStatus(id, payload);
      setOrders((arr) => arr.map((o) => (o.id === id ? { ...o, ...data } : o)));
      if (selected?.id === id) setSelected({ ...selected, ...data });
      toast.success('Pedido atualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  const handleExportXlsx = async () => {
    try {
      const { data } = await exportAPI.orders({ format: 'xlsx' });
      downloadBlob(data, `pedidos-destack-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      toast.error('Falha ao exportar Excel');
    }
  };

  const handleExportPdf = async () => {
    try {
      const { data } = await exportAPI.ordersPdf();
      downloadBlob(data, `pedidos-destack-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch {
      toast.error('Falha ao exportar PDF');
    }
  };

  return (
    <div className="space-y-5" data-testid="orders-central-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Central de Pedidos</h1>
          <p className="text-slate-500 mt-1">
            Acompanhe todos os pedidos do marketplace em um só lugar.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExportXlsx} variant="outline" data-testid="export-xlsx-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
            Excel
          </Button>
          <Button onClick={handleExportPdf} variant="outline" data-testid="export-pdf-btn">
            <FileBarChart className="w-4 h-4 mr-2 text-rose-600" />
            PDF
          </Button>
          <Button onClick={reload} variant="outline" data-testid="reload-orders">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Total de Pedidos" value={stats.total} accent="blue" />
        <KpiCard label="Faturamento" value={fmtBRL(stats.revenue)} accent="emerald" />
        <KpiCard label="Aguardando Pagto" value={stats.pending} accent="amber" />
        <KpiCard label="Pagto Confirmado" value={stats.confirmed} accent="violet" />
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por protocolo, cliente, loja ou telefone..."
                className="pl-10"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                data-testid="filter-text"
              />
            </div>
            <select
              value={filterPayment}
              onChange={(e) => setFilterPayment(e.target.value)}
              className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              data-testid="filter-payment"
            >
              <option value="all">Pagamento — Todos</option>
              {Object.entries(STATUS_PAYMENT).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              data-testid="filter-status"
            >
              <option value="all">Status — Todos</option>
              {Object.entries(STATUS_ORDER).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="text-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Package className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              Nenhum pedido encontrado.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold text-slate-700">Protocolo</th>
                  <th className="px-4 py-3 font-semibold text-slate-700">Cliente</th>
                  <th className="px-4 py-3 font-semibold text-slate-700">Loja</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 text-right">Total</th>
                  <th className="px-4 py-3 font-semibold text-slate-700">Pagto</th>
                  <th className="px-4 py-3 font-semibold text-slate-700">Status</th>
                  <th className="px-4 py-3 font-semibold text-slate-700">Data</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((o) => {
                  const pay = STATUS_PAYMENT[o.payment_status || 'pending'] || STATUS_PAYMENT.pending;
                  const ord = STATUS_ORDER[o.order_status || 'received'] || STATUS_ORDER.received;
                  return (
                    <tr key={o.id} className="hover:bg-slate-50" data-testid={`order-row-${o.protocol}`}>
                      <td className="px-4 py-3 font-mono text-xs text-blue-700">{o.protocol}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 truncate max-w-[200px]">{o.customer_name}</div>
                        <div className="text-xs text-slate-500">{o.customer_phone}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700 truncate max-w-[180px]">{o.store_name}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmtBRL(o.total)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${pay.cls}`}>
                          {pay.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${ord.cls}`}>
                          {ord.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {o.created_at ? new Date(o.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => setSelected(o)} data-testid={`view-order-${o.protocol}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-600" />
                  <span className="font-mono text-blue-700">{selected.protocol}</span>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Cliente</p>
                    <p className="font-medium text-slate-900">{selected.customer_name}</p>
                    <p className="text-slate-600 inline-flex items-center gap-1 mt-0.5">
                      <Phone className="w-3.5 h-3.5" /> {selected.customer_phone}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Loja</p>
                    <p className="font-medium text-slate-900 inline-flex items-center gap-1">
                      <StoreIcon className="w-3.5 h-3.5" /> {selected.store_name}
                    </p>
                    {selected.store_whatsapp && (
                      <p className="text-emerald-600 text-xs mt-0.5">WhatsApp loja: {selected.store_whatsapp}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Endereço</p>
                    <p className="text-slate-900 inline-flex items-start gap-1">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>{selected.customer_address}</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Data/Hora</p>
                    <p className="text-slate-900 inline-flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {selected.created_at ? new Date(selected.created_at).toLocaleString('pt-BR') : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Pagamento</p>
                    <p className="font-medium text-slate-900">{selected.payment_method || 'PIX'}</p>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Produtos</p>
                  <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {(selected.items || []).map((it, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 text-sm">
                        <div>
                          <p className="font-medium text-slate-900">{it.name}</p>
                          <p className="text-xs text-slate-500">Qtd: {it.quantity}</p>
                        </div>
                        <div className="font-semibold text-slate-900">
                          {fmtBRL((it.price || 0) * (it.quantity || 1))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-2 px-2">
                    <span className="text-slate-500 text-sm">Total</span>
                    <span className="font-bold text-lg text-slate-900">{fmtBRL(selected.total)}</span>
                  </div>
                </div>

                {/* PIX */}
                {selected.store_pix_key && (
                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Chave PIX da loja</p>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <p className="font-mono text-sm text-slate-800 break-all">{selected.store_pix_key}</p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(selected.store_pix_key);
                          toast.success('Chave copiada');
                        }}
                        className="p-1.5 rounded hover:bg-slate-100 flex-shrink-0"
                        data-testid="copy-store-pix"
                      >
                        <Copy className="w-4 h-4 text-slate-500" />
                      </button>
                    </div>
                    {selected.store_pix_holder && (
                      <p className="text-xs text-slate-500 mt-1">Beneficiário: {selected.store_pix_holder}</p>
                    )}
                  </div>
                )}

                {selected.notes && (
                  <div className="rounded-lg border border-slate-200 bg-amber-50 p-3 text-sm">
                    <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-1">Observações do cliente</p>
                    <p className="text-amber-900">{selected.notes}</p>
                  </div>
                )}

                {/* Quick actions */}
                <div className="border-t border-slate-200 pt-4 space-y-2">
                  <p className="text-xs uppercase tracking-wider text-slate-500">Atualizar Status</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => patchOrder(selected.id, { payment_status: 'paid_unverified' })}
                      data-testid="mark-paid-unverified"
                    >
                      Cliente afirma pago
                    </Button>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => patchOrder(selected.id, { payment_status: 'confirmed' })}
                      data-testid="mark-confirmed"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Pagamento confirmado
                    </Button>
                    <Button
                      size="sm"
                      className="bg-violet-600 hover:bg-violet-700"
                      onClick={() => patchOrder(selected.id, { order_status: 'forwarded' })}
                      data-testid="mark-forwarded"
                    >
                      <Truck className="w-4 h-4 mr-1" /> Encaminhado p/ loja
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => patchOrder(selected.id, { order_status: 'delivered' })}
                      data-testid="mark-delivered"
                    >
                      Entregue
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-rose-600 border-rose-200"
                      onClick={() => patchOrder(selected.id, { order_status: 'cancelled' })}
                      data-testid="mark-cancelled"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Cancelar
                    </Button>
                  </div>
                </div>

                {/* Open WhatsApps + Convite pós-compra */}
                <div className="border-t border-slate-200 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {selected.store_whatsapp && (
                    <a
                      href={`https://wa.me/55${(selected.store_whatsapp || '').replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700"
                      data-testid="open-store-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4" /> Abrir WhatsApp da loja
                    </a>
                  )}
                  {selected.customer_phone && (
                    <a
                      href={`https://wa.me/55${(selected.customer_phone || '').replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
                      data-testid="open-customer-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4" /> Abrir WhatsApp do cliente
                    </a>
                  )}
                  {(selected.order_status === 'delivered' || selected.status === 'delivered' || selected.payment_status === 'confirmed') && (
                    <div className="sm:col-span-2 flex flex-col gap-2">
                      {selected.invite_status && (
                        <div
                          className={`rounded-lg px-3 py-2 text-xs font-semibold border flex items-center justify-between gap-2 ${
                            selected.invite_status === 'generated'
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                              : selected.invite_status === 'scheduled'
                              ? 'bg-amber-50 border-amber-200 text-amber-700'
                              : 'bg-slate-50 border-slate-200 text-slate-600'
                          }`}
                          data-testid="invite-status-badge"
                        >
                          <span>
                            Convite pós-compra:{' '}
                            {selected.invite_status === 'scheduled'
                              ? `agendado para ${new Date(selected.invite_scheduled_at).toLocaleString('pt-BR')}`
                              : selected.invite_status === 'generated'
                              ? `pronto (${new Date(selected.invite_generated_at).toLocaleString('pt-BR')})`
                              : selected.invite_status === 'skipped_no_phone'
                              ? 'sem telefone cadastrado — não enviado'
                              : selected.invite_status}
                          </span>
                          <button
                            onClick={async () => {
                              try {
                                await reviewsAPI.scheduleInvite(selected.id, 0);
                                toast.success('Convite reagendado para agora (será processado em até 5 min)');
                                await reload();
                                const fresh = await ordersAPI.list();
                                const updated = (fresh.data || []).find((o) => o.id === selected.id);
                                if (updated) setSelected(updated);
                              } catch (e) { toast.error('Erro ao reagendar'); }
                            }}
                            className="text-[10px] uppercase tracking-widest bg-white/60 px-2 py-0.5 rounded-full"
                            data-testid="reschedule-invite-btn"
                          >Reagendar</button>
                        </div>
                      )}
                      <button
                        onClick={async () => {
                          try {
                            const { data } = await reviewsAPI.adminInvite(selected.id);
                            if (data.whatsapp_url) {
                              window.open(data.whatsapp_url, '_blank', 'noopener');
                            } else {
                              navigator.clipboard.writeText(`${data.message}\n\n${data.invite_url}`);
                              toast.success('Mensagem copiada (cliente sem telefone).');
                            }
                          } catch (e) {
                            toast.error('Erro ao gerar convite');
                          }
                        }}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 text-sm font-semibold"
                        data-testid="send-review-invite-btn"
                      >
                        <StarIcon className="w-4 h-4" /> Convidar cliente a avaliar (WhatsApp)
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const KpiCard = ({ label, value, accent = 'blue' }) => {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    emerald: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-amber-600',
    violet: 'from-violet-500 to-violet-600',
  };
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white border border-slate-200 p-4">
      <div className={`absolute right-0 top-0 h-1 w-full bg-gradient-to-r ${colors[accent]}`} />
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
    </div>
  );
};

export default OrdersCentral;
